import { axiosInstance } from './api';
import type { FrameBox } from '@/components/ui/frame-with-polygon';

/** Тяжёлые выборки за период (месяц) — дольше общего таймаута 30 с (#3390). */
const HEAVY_TIMEOUT_MS = 120_000;

export interface TimeseriesPoint {
  date: string;
  value: number;
}

export interface TaskTimeseriesPoint {
  date: string;
  total: number;
  done: number;
  rate: number;
}

/**
 * Period-actual worst-moment evidence for a non-OK rack_type group. Sourced
 * from ValidationMessage (validated problem events) so the photo shown for
 * "Готовая еда" really comes from a Готовая еда rack at a real flagged
 * moment in this period. Null on healthy groups (no problem to surface).
 */
export interface LayoutWorstSample {
  image_url: string | null;
  rack_name: string;
  fill_pct: number | null;
  at: string;       // ISO timestamp
  status: string;   // RackStatus value (NOTICEABLY_EMPTY / ALMOST_EMPTY)
}

export interface LayoutGroup {
  /** Server-side category label, e.g. "Выпечка", "Готовая еда". Empty string for ungrouped racks. */
  rack_type: string;
  rack_count: number;
  avg_fill_pct: number | null;
  prev_avg_fill_pct: number | null;
  peak_fill_pct: number | null;
  worst_fill_pct: number | null;
  delta_pct: number | null;
  timeseries: TimeseriesPoint[];
  worst_sample: LayoutWorstSample | null;
  /** Validated problem-status events (NOTICEABLY_EMPTY / ALMOST_EMPTY) in this period. */
  problem_event_count: number;
  /** Sum of resolved problem durations in minutes. */
  problem_total_minutes: number;
  /** Longest single resolved problem duration in minutes. */
  problem_longest_minutes: number;
}

export interface LayoutAnalytics {
  configured: boolean;
  /** Zone-overall headline numbers (weighted by rack_count across groups). */
  avg_fill_pct: number | null;
  prev_avg_fill_pct: number | null;
  peak_fill_pct: number | null;
  worst_fill_pct: number | null;
  delta_pct: number | null;
  timeseries: TimeseriesPoint[];
  /** Zone-overall event-based metrics — what the manager actually wants. */
  problem_event_count: number;
  problem_total_minutes: number;
  problem_longest_minutes: number;
  problem_worst_fill_pct: number | null;
  /** Per-rack_type breakdown surfaced as expandable rows below the headline. */
  groups: LayoutGroup[];
  /** Period-scoped Grafana drill-in URL for this tab; null if no dashboard wired. */
  grafana_url: string | null;
}

export interface QueueAnalytics {
  configured: boolean;
  avg_people: number | null;
  prev_avg_people: number | null;
  peak_people: number | null;
  delta: number | null;
  timeseries: TimeseriesPoint[];
  /** Period-scoped Grafana drill-in URL for this tab; null if no dashboard wired. */
  grafana_url: string | null;
}

export interface CleanupHistogram {
  le_2m: number;
  '2_5m': number;
  '5_15m': number;
  gt_15m: number;
}

export interface TablesAnalytics {
  total_tables: number;
  /** Cleanup time = TVM.created_at → first subsequent ML detection where dirty_dishes_table=False. */
  median_cleanup_s: number | null;
  p90_cleanup_s: number | null;
  prev_median_cleanup_s: number | null;
  delta: number | null;
  cleanup_histogram: CleanupHistogram | null;
  timeseries: TimeseriesPoint[];
  /** Confirmed-real alerts in the period (resolved + unresolved within search window). */
  alert_count?: number;
  /** Confirmed alerts whose ML re-detection didn't fire within (period_end + 24 h). */
  unresolved_count?: number;
}

export interface ChronicFailItem {
  /** First ChecklistItem PK in the merged group — kept as a stable React key. */
  checklist_item_id: number;
  /** All ChecklistItem PKs in this digest row (multiple when the same task
   *  fires on different weekdays via separate ChecklistItems). FE drill-in
   *  fans back to all of them so the expand panel covers every execution. */
  checklist_item_ids: number[];
  task_text: string;
  /** "HH:MM" of ChecklistItem.due_time. Used to disambiguate same-name items. */
  due_time: string | null;
  completion_rate: number;
  total: number;
  done: number;
}

export interface TaskTimingHistogram {
  /** done >= 30 min before deadline */
  early: number;
  /** within 30 min of deadline (either side) */
  on_time: number;
  /** 30 min – 2 h after deadline */
  late_short: number;
  /** > 2 h after deadline */
  late_long: number;
}

export interface TasksAnalytics {
  completion_rate: number | null;
  prev_completion_rate: number | null;
  delta_pct: number | null;
  total: number;
  done: number;
  /** Share of done tasks completed at or close to the deadline (early + on_time). */
  on_time_rate: number | null;
  prev_on_time_rate: number | null;
  on_time_delta_pct: number | null;
  /** Distribution of done tasks by lateness; null when there were no done tasks. */
  timing_histogram: TaskTimingHistogram | null;
  timeseries: TaskTimeseriesPoint[];
  chronic_fail: ChronicFailItem[];
  top_done: ChronicFailItem[];
  /** Per-employee submission rating, already sorted desc by `submitted`.
   *  Absent on older BE responses. */
  by_reporter?: TaskReporterStat[];
}

/** Who submitted how many task reports in the period. `closed` is the subset
 *  that actually closed its task — several follow-up photos on one task are not
 *  the same contribution as several completed tasks. */
export interface TaskReporterStat {
  username: string;
  submitted: number;
  closed: number;
  /** Which tasks this person submitted and how often, desc by count. Grouped by
   *  task TEXT (the same task recurs daily and across weekday ChecklistItems),
   *  with every underlying ChecklistItem id kept so the drill-in can fan out to
   *  all executions. Absent on older BE responses. */
  tasks?: TaskReporterTaskStat[];
}

export interface TaskReporterTaskStat {
  task_text: string;
  submitted: number;
  checklist_item_ids: number[];
}

export type AnalyticsTab = 'layout' | 'queue' | 'tables' | 'tasks';

/**
 * Per-tab analytics response: only the requested tab's block is populated.
 * The endpoint is intentionally one-tab-at-a-time so a slow domain (e.g.
 * Layout for a zone with months of frames) doesn't block the others on
 * first page load.
 */
export interface ZoneAnalyticsTabResponse {
  grafana_dashboard_url: string | null;
  granularity: string;
  layout?: LayoutAnalytics;
  queue?: QueueAnalytics;
  tables?: TablesAnalytics;
  tasks?: TasksAnalytics;
}

export const getZoneAnalyticsTab = (
  zoneId: string,
  tab: AnalyticsTab,
  from?: string,
  to?: string,
  granularity?: string,
) =>
  axiosInstance.get<ZoneAnalyticsTabResponse>('/core/api/analytics/zone-summary/', {
    params: { zone_id: zoneId, tab, from, to, granularity },
  });

// ── Day findings (trusted per-area verdicts for the unified День report) ────────
//
// Computed with the SAME logic as the daily TG report (shelf-shortage intervals,
// true queue peak), so the web report and the message never disagree. The stats
// tab endpoints aggregate hourly AVG (good for charts, wrong for verdicts), so
// the report reads these instead.

export interface FindingsPhoto {
  image_url: string | null;
  caption?: string;
  at?: string | null;
  fill_pct?: number | null;
  captured_occupancy?: number | null;
  rack_name?: string;
}

export interface LayoutCategoryFinding {
  rack_type: string;
  status: 'red' | 'yellow' | 'green';
  shortage_pct: number | null;
  problem_hours: number | null;
  shelf_count: number | null;
}

export interface LayoutFindings {
  configured: boolean;
  status?: 'red' | 'yellow' | 'green';
  worst?: LayoutCategoryFinding | null;
  worst_rack_name?: string | null;
  categories?: LayoutCategoryFinding[];
  photo?: FindingsPhoto | null;
  timeseries?: TimeseriesPoint[];
  /** Per-shelf stacked timeline of the worst rack — the loved at-a-glance chart. */
  shelf_timeseries?: LayoutDetailShelfTimeseries;
  grafana_url?: string | null;
}

export interface QueueFindings {
  configured: boolean;
  status?: 'red' | 'yellow' | 'green';
  peak_people?: number;
  peak_at?: string | null;
  decrease_min?: number | null;
  idle_hours?: number;
  open_hours?: number;
  long_queue_events?: number | null;
  /** Минуты с очередью от 4 человек — порог отчёта дня (30/60 мин). Нет на старом BE. */
  long_queue_minutes?: number | null;
  second_cashier_events?: number | null;
  second_cashier_minutes?: number | null;
  avg_people?: number | null;
  photo?: FindingsPhoto | null;
  timeseries?: TimeseriesPoint[];
  grafana_url?: string | null;
}

export interface DayFindingsResponse {
  closed: boolean;
  layout: LayoutFindings;
  queue: QueueFindings;
  tables: TablesAnalytics | null;
  tasks: TasksAnalytics | null;
  grafana_dashboard_url?: string | null;
}

/** Окно дневных вердиктов для раздела «Статус» одним запросом (снимки DayFindingsSnapshot;
 *  недостающие прошедшие дни BE досчитывает, не больше 16 за запрос → partial). */
export interface StatusWindowDay { day: string; findings: DayFindingsResponse | null }
export interface StatusWindowResponse { zone_id: number; days: StatusWindowDay[]; partial: boolean }
export const getStatusWindow = (zoneId: string, days = 14, offset = 1) =>
  axiosInstance.get<StatusWindowResponse>('/core/api/analytics/status-window/', { params: { zone_id: zoneId, days, offset } });

/** Эпизоды просадок выкладки (Алексей 21.10): один непрерывный интервал «ниже нормы»
 *  стеллажа — та же детекция, что у отчёта дня. `queue` — люди в зоне очереди за
 *  время просадки: человеко-минуты (минуты без кадров не считаются) и среднее;
 *  null — у зоны нет очереди. */
export interface DipQueue { person_minutes: number; covered_minutes: number; avg_people: number | null }
export interface DipEpisode {
  rack_id: number; rack_name: string; rack_type: string | null;
  start: string; end: string; minutes: number; missing_pct: number;
  photos: { image_url: string; at: string | null; fill_pct: number | null }[];
  queue: DipQueue | null;
}
/** `queue` у дня — по объединению просадок всех стеллажей (одновременные не дважды). */
export interface DipDay { day: string; episodes: DipEpisode[]; queue_configured: boolean; queue: DipQueue | null }
/** Чеки за время просадок (#2507): пробито, из них с позицией категории стеллажа, сколько было бы при обычной доле
 *  в те же часы таких же дней за 4 недели. null — у зоны нет касс. */
export interface DipReceiptsCounts { checks: number; with_category: number; expected_with_category: number }
export interface DipReceipts extends DipReceiptsCounts { by_type?: Record<string, DipReceiptsCounts> }
export interface LayoutDipsResponse { zone_id: number; queue_configured: boolean | null; days: DipDay[]; total_queue_person_minutes: number | null; receipts?: DipReceipts | null }
export const getLayoutDips = (zoneId: string | number, from: string, to: string, rackId?: number | null) =>
  axiosInstance.get<LayoutDipsResponse>('/core/api/analytics/layout-dips/', {
    params: { zone_id: zoneId, from, to, ...(rackId != null && rackId > 0 ? { rack_id: rackId } : {}) },
  });

/** Эпизоды длинной очереди сериями кадров (Алексей 21.10): инциденты «Очередь от 4
 *  человек» отчёта дня (сумма `minutes` за день = минуты отчёта). Кадры — только с
 *  видимой очередью: начало → пик (`is_peak`) → конец. `drain_min` — «спала за» от пика,
 *  null — спад не наблюдался. `top` — только N главных эпизодов периода. */
export interface QueueEpisode {
  start: string; end: string; minutes: number;
  peak_people: number; peak_at: string; drain_min: number | null;
  photos: { image_url: string; at: string; people: number; is_peak: boolean }[];
}
export interface QueueEpisodeDay { day: string; configured: boolean; episodes_count: number; minutes_total: number; episodes: QueueEpisode[] }
export interface QueueEpisodesResponse { zone_id: number; configured: boolean; threshold: number | null; episodes_total: number; days: QueueEpisodeDay[] }
export const getQueueEpisodes = (zoneId: string | number, from: string, to: string, top?: number) =>
  axiosInstance.get<QueueEpisodesResponse>('/core/api/analytics/queue-episodes/', {
    params: { zone_id: zoneId, from, to, ...(top != null ? { top } : {}) },
  });

/** «Все филиалы» для «Статуса»: выжимка снимков без фото и таймсерий, только сохранённые дни. */
export interface StatusAllZone { zone_id: number; name: string; days: StatusWindowDay[] }
export interface StatusAllResponse { from: string; to: string; zones: StatusAllZone[] }
export const getStatusAll = (days = 14, offset = 1) =>
  axiosInstance.get<StatusAllResponse>('/core/api/analytics/status-all/', { params: { days, offset } });

export const getDayFindings = (zoneId: string, day: string) =>
  axiosInstance.get<DayFindingsResponse>('/core/api/analytics/day-findings/', {
    params: { zone_id: zoneId, day },
  });

// ── Layout detail (per-rack deep inspection) ──────────────────────────────────
//
// One rack at a time. The aggregated layout tab averaged across rack_types
// and the result was always "body-temperature 75-85%", which hid the dips
// that managers actually care about. The detail view shows the rack's
// individual shelves over time plus 6 evenly-spaced rack photos so a dip
// on the chart can be cross-checked against what was physically on the
// shelves at that moment.

export interface LayoutDetailRack {
  id: number;
  name: string;
  rack_type: string | null;
}

export interface LayoutDetailShelf {
  id: number;
  name: string;
  shelf_num_on_rack: number;
  /** Alert threshold, 0-1 (DB scale), null on older BE responses. A shelf at 0
   *  is deliberately not analysed (second bakery row etc.) and must be skipped
   *  in "below threshold" math, not counted as permanently below. */
  empty_shelf_score?: number | null;
}

export interface LayoutDetailShelfPoint {
  date: string;
  values: Record<number, number | null>;
}

export interface LayoutDetailShelfTimeseries {
  shelves: LayoutDetailShelf[];
  points: LayoutDetailShelfPoint[];
  /** Ступени вечернего понижения порога (RackThresholdSchedule) за период: на каждую местную
   *  дату полночь ×1, затем ступени вечера. Пусто/нет — расписания нет (Алексей 21.10 #1669). */
  threshold_steps?: { at: string; multiplier: number }[];
}

export interface LayoutDetailPhoto {
  image_url: string | null;
  /** Полный кадр, если сохранился (для «в оригинальных пропорциях», #1671). */
  frame_image_url?: string | null;
  at: string; // ISO timestamp
  fill_pct: number | null;
}

export interface LayoutDetailHourRhythmPoint {
  hour: number; // 0-23, MSK-shifted
  value: number | null;
}

/** Working-hours window for a single-day request; null on multi-day. */
export interface WorkingHours {
  start: string; // tz-aware ISO — REAL open (timing: open time / keypoints / correction math)
  end: string;   // tz-aware ISO — REAL close
  /** ±30-padded window for the chart's VISUAL x-domain breathing room (888).
   *  Absent on older BE responses → callers fall back to start/end. NEVER use
   *  these for timing — that's what start/end are for. */
  domain_start?: string;
  domain_end?: string;
}

/** The chart's VISUAL x-domain [start,end] as ISO strings: the ±30-padded
 *  window when the BE supplies it, else the raw open/close. Timing code must
 *  read wh.start/end directly, NOT this (888). */
export function whDomain(wh: WorkingHours): { start: string; end: string } {
  return { start: wh.domain_start ?? wh.start, end: wh.domain_end ?? wh.end };
}

/** One report-auto-detected shortage window for this rack — item A (2026-09):
 *  the SAME detection `compute_overall_performance` already runs for the
 *  report text (`compute_rack_auto_segments`, scoped to this rack), so the
 *  Интерактив F1 chart can prefill instead of starting blank. */
export interface LayoutDetailAutoSegment {
  t_start: string; // ISO
  t_end: string; // ISO
  missing_pct: number; // bucketed 10..100
}

export interface LayoutDetailResponse {
  racks: LayoutDetailRack[];
  selected_rack: LayoutDetailRack | null;
  shelf_timeseries: LayoutDetailShelfTimeseries;
  rack_photos: LayoutDetailPhoto[];
  hour_rhythm: LayoutDetailHourRhythmPoint[];
  /** Present (single-day) → chart X domain; null on multi-day. */
  working_hours?: WorkingHours | null;
  /** Absent on older BE responses / multi-day requests. */
  auto_segments?: LayoutDetailAutoSegment[];
  grafana_url?: string | null;
}

export interface LayoutDetailParams {
  rackId?: number | string;
  granularity?: string;
  /** "dip" → worst-stocked frame per time-bin (day card); "problems" →
   *  empty-shelf incidents first then even spread (Стата «Выкладка»); default "even". */
  photoMode?: 'even' | 'dip' | 'problems';
  photoCount?: number;
}

/** Полоса метрик над «Выкладкой»: по каждому стеллажу за период — средний
 *  процент и минуты ниже нормы (BE analytics.zone_layout_racks_summary). */
export interface LayoutRackSummary {
  id: number;
  name: string;
  rack_type: string | null;
  avg_pct: number | null;
  /** null при дневном шаге (часы там бессмысленны) или без данных. */
  minutes_below: number | null;
  /** Чем меряли «ниже нормы»: правило отчёта дня (единое с «Статусом», #1453), либо
   *  запасные — пороги полок (Shelf.empty_shelf_score) / средний процент. */
  below_rule?: 'day_report' | 'shelf_thresholds' | 'avg_below_pct';
  /** Цвет стеллажа по правилу отчёта дня: худший день периода. */
  status?: 'green' | 'yellow' | 'red' | null;
  /** Открытых дней, по которым посчитаны часы правила отчёта дня (для «N ч в день»). */
  days?: number | null;
  shelf_thresholds?: number[];
  total_minutes: number | null;
  buckets: number;
}
export interface LayoutRacksSummaryResponse {
  racks: LayoutRackSummary[];
  /** Худший цвет категорий за период — им красится «Все вместе» (#1453). */
  overall_status?: 'green' | 'yellow' | 'red' | null;
  /** Не все дни периода посчитаны (лимит на запрос) — цифры неполные. */
  partial?: boolean;
  granularity: '15min' | 'hour' | 'day';
  below_pct: number;
}
export const getLayoutRacksSummary = (zoneId: string, from: string, to: string, belowPct?: number) =>
  axiosInstance.get<LayoutRacksSummaryResponse>('/core/api/analytics/layout-racks-summary/', {
    params: { zone_id: zoneId, from, to, below_pct: belowPct },
    // Месяц «холодным» считается до ~1 мин; на общих 30 с плашка «часть данных не загрузилась»
    // висела при живом сервере (Алексей 30.10 #3390).
    timeout: HEAVY_TIMEOUT_MS,
  });

export const getLayoutDetail = (
  zoneId: string,
  from: string,
  to: string,
  rackId?: number | string,
  granularity?: string,
  opts?: { photoMode?: 'even' | 'dip' | 'problems'; photoCount?: number; fromTs?: string; toTs?: string },
) =>
  axiosInstance.get<LayoutDetailResponse>('/core/api/analytics/layout-detail/', {
    timeout: HEAVY_TIMEOUT_MS,
    params: {
      zone_id: zoneId,
      from,
      to,
      rack_id: rackId,
      granularity,
      photo_mode: opts?.photoMode,
      photo_count: opts?.photoCount,
      // Optional sub-day zoom window → denser frames for just that period.
      from_ts: opts?.fromTs,
      to_ts: opts?.toTs,
    },
  });

// ── Queue detail (queue+kitchen+cashier overlay + histogram + photos) ─────────
//
// Mirrors the Grafana queue dashboard's deep-inspection visuals so an
// operator can see queue length over time alongside kitchen workers, open
// cashiers, and waiting-area people, plus the time-spent-at-N-people
// histogram which exposes idle minutes (the N=0 bucket).

export interface QueuePolygonIds {
  queue: number[];
  cashier: number[];
  kitchen: number[];
  waiting: number[];
}

export interface QueueOverlayPoint {
  time: string; // ISO datetime
  queue: number;
  kitchen: number;
  cashier_count: number;
  waiting: number;
  /** Venues with 2+ named queues: per-queue sums as `queue_<checkoutZoneId>`. */
  [perQueue: `queue_${number}`]: number | undefined;
}

export interface QueueHistogramBucket {
  people: number;
  minutes: number;
}

export interface QueuePhoto {
  image_url: string | null;
  at: string;
  captured_occupancy: number;
}

export interface QueueWaitTimePoint {
  time: string;
  seconds: number | null;
}

export interface QueueWorkerMatrixCell {
  kitchen: number;
  front_of_register: number;
  minutes: number;
}

/** How long a queue of each length persists once it forms — the answer to
 *  «задерживаются ли длинные очереди надолго?». Computed on the BACKEND from raw
 *  15-second buckets: the overlay that reaches the FE is already aggregated to
 *  ~200 points, so short episodes would vanish if we derived this here. */
export interface QueueDwellCell {
  length: number;
  episodes: number;
  /** Среднее время одного эпизода — то, что показывается на карточке. Медиана
   *  на этих данных почти всегда равна интервалу съёмки (эпизодов «пришли-ушли
   *  между двумя кадрами» больше половины), поэтому она показывала одинаковые
   *  33 с на всех спотах; среднее различает загруженную очередь от спокойной.
   *  Optional: старые/кешированные ответы его не несут. */
  mean_seconds?: number;
  /** Сумма длительностей всех эпизодов этой длины за период — колонка «в сумме».
   *  Optional: старые ответы его не несут, тогда считаем episodes × mean. */
  total_seconds?: number;
  median_seconds: number;
  max_seconds: number;
  /** Sampling resolution for the zone (median gap between frames). A duration
   *  equal to it means "gone between two frames", not a measured length —
   *  render it as such instead of a precise number. Optional: older responses
   *  (and cached ones) don't carry it. */
  floor_seconds?: number;
}

export interface QueueDecreaseSpeedCell {
  length: number;
  decrease: number;
  minutes: number;
}

/** Queue-family area whose minutes×(N people) histogram can be shown. */
export type QueueArea = 'queue' | 'kitchen' | 'waiting';

export interface QueueAreaHistogram {
  area: QueueArea;
  buckets: QueueHistogramBucket[];
  idle_minutes: number;
}

export interface QueueDetailResponse {
  configured: boolean;
  polygons: QueuePolygonIds;
  interval_ms: number;
  overlay: QueueOverlayPoint[];
  length_histogram: QueueHistogramBucket[];
  idle_minutes: number;
  /** Per-area histograms (queue/kitchen/waiting that exist) for the area toggle. */
  area_histograms?: QueueAreaHistogram[];
  queue_photos: QueuePhoto[];
  /** One per day: that day's longest queue (from daily-report highlights). */
  longest_per_day: QueuePhoto[];
  wait_time: QueueWaitTimePoint[];
  worker_matrix: QueueWorkerMatrixCell[];
  decrease_speed: QueueDecreaseSpeedCell[];
  /** Absent on older BE responses. */
  dwell_by_length?: QueueDwellCell[];
  /** Present (single-day) → chart X domain; null on multi-day. */
  working_hours?: WorkingHours | null;
  grafana_url?: string | null;
  /** 2+ named queues (касса + самообслуживание) → the overlay carries a
   *  queue_<id> per-queue series for each; empty/absent otherwise. */
  queues?: Array<{ id: number; name: string }>;
  /** Порог очереди (max_allowed_size): больше — красный, ровно — жёлтый (#1671). */
  queue_limits?: Array<{ id: number; name: string; max_allowed: number }>;
  /** Приход сотрудников к открытию (#1324): один день, когда функция включена; иначе null/absent. */
  staff_arrival?: StaffArrivalDayDto | null;
}

// ── Приход сотрудников к открытию (#1324) ─────────────────────────────────────
export type StaffArrivalStatus = 'early' | 'on_time' | 'late' | 'pending' | 'nobody' | 'no_frames';
export interface StaffArrivalDayDto {
  id: number;
  date: string;
  open_at: string;
  expected_at: string;
  capture_from: string;
  first_seen_at: string | null;
  first_seen_people: number;
  /** Минуты от назначенного времени (+ позже, − раньше); null — никого. */
  delta_min: number | null;
  /** «на 7 мин раньше назначенного времени» и т.п.; null — ещё ждём. */
  text: string | null;
  /** Уже были на первом кадре окна, начатого вовремя: к назначенному времени на месте (#2324). */
  already?: boolean;
  status: StaffArrivalStatus;
  photo_url: string | null;
  photo_at: string | null;
  /** Фото на назначенное время (опоздание/«никого»), а не кадр прихода (Алексей 26.10 #2604). */
  photo_is_start?: boolean;
  arrival_photo_url?: string | null;
  arrival_photo_at?: string | null;
  kuku_sent_at: string | null;
  we_are_here_found: boolean | null;
}
export interface StaffArrivalResponse {
  enabled: boolean;
  minutes_default: number;
  from: string;
  to: string;
  days: StaffArrivalDayDto[];
  summary: { days: number; with_people: number; nobody: number; late: number; early_or_on_time: number; avg_delta_min: number | null; max_late_min: number | null };
}
export const getStaffArrival = (zoneId: string, from: string, to: string) =>
  axiosInstance.get<StaffArrivalResponse>(`/core/api/zones/${zoneId}/staff-arrival/`, { params: { from, to } });

/** Average + peak estimated wait, fetched separately from queue-detail: the
 *  per-minute series was removed from that endpoint for latency, so `wait_time`
 *  there is always empty. Only the aggregate crosses the wire here. */
export interface QueueWaitStat {
  avg_seconds: number | null;
  max_seconds: number | null;
  samples: number;
}

export interface QueueWaitSummary extends QueueWaitStat {
  /** ORDER_WAITING zone wait («в зоне ожидания», Grafana window 40); the flat
   *  fields above are the QUEUE metric («в очереди», window 9). Absent on older
   *  BE responses. */
  waiting?: QueueWaitStat;
}

export const getQueueWaitSummary = (zoneId: string, from: string, to: string) =>
  axiosInstance.get<QueueWaitSummary>('/core/api/analytics/queue-wait-summary/', {
    params: { zone_id: zoneId, from, to },
  });

export const getQueueDetail = (
  zoneId: string,
  from: string,
  to: string,
  intervalMs?: number,
  opts?: { photoMode?: 'even' | 'peak'; photoCount?: number; fromTs?: string; toTs?: string },
) =>
  axiosInstance.get<QueueDetailResponse>('/core/api/analytics/queue-detail/', {
    timeout: HEAVY_TIMEOUT_MS,
    params: {
      zone_id: zoneId,
      from,
      to,
      interval_ms: intervalMs,
      photo_mode: opts?.photoMode,
      photo_count: opts?.photoCount,
      // Optional sub-day zoom window → denser frames for just that period.
      from_ts: opts?.fromTs,
      to_ts: opts?.toTs,
    },
  });

// ── Tables detail (per-table occupancy + flags) ──────────────────────────────
//
// Existing cleanup-time KPIs (median / p90 / cleanup histogram) live on
// the older /zone-summary/?tab=tables endpoint. This complements them with
// per-table deep inspection: pick one table, see its occupancy over time,
// 99th-percentile peak, people-count histogram, and the share of frames
// where the table was occupied / dirty / had food on it.

export interface TableDetailTable {
  id: number;
  name: string;
}

export interface TableDetailTimePoint {
  time: string;
  captured: number;
}

export interface TableDetailHistogramBucket {
  people: number;
  minutes: number;
}

export interface TableDetailStateBreakdown {
  frames: number;
  occupied_pct: number;
  dirty_pct: number;
  food_pct: number;
  empty_pct: number;
}

export interface TableDetailResponse {
  tables: TableDetailTable[];
  selected_table: TableDetailTable | null;
  polygon_id: number | null;
  timeseries: TableDetailTimePoint[];
  max_p99: number | null;
  histogram: TableDetailHistogramBucket[];
  state: TableDetailStateBreakdown;
  interval_ms: number;
}

export const getTablesDetail = (
  zoneId: string,
  from: string,
  to: string,
  tableId?: number | string,
  intervalMs?: number,
) =>
  axiosInstance.get<TableDetailResponse>('/core/api/analytics/tables-detail/', {
    params: { zone_id: zoneId, from, to, table_id: tableId, interval_ms: intervalMs },
  });

// ── Queue: worker-matrix example frames ───────────────────────────────────────

export interface QueueWorkerExample {
  image_url: string;
  at: string;
  frame_id: number;
  /** Кадр бара с другой камеры, ближайший по времени к кадру очереди (L2);
   *  null — одна камера на обе зоны или пары в пороге нет. */
  pair?: { image_url: string; at: string; frame_id: number; gap_seconds: number } | null;
}

/** Эпизоды «не хватало рук» (у кассы от front_min, за баром ≤ kitchen_max) — доказательство в Статусе →
 *  «Размер смен» (Алексей 26.10 #2623/#2625). */
export type QueueShortEpisode = {
  start: string; end: string; minutes: number; front_max: number; kitchen_max: number;
  photos: { image_url: string; at: string; what: 'front' | 'kitchen' }[];
};
export type QueueShortEpisodesResponse = {
  episodes: QueueShortEpisode[]; total_minutes: number; days: number;
  episodes_count?: number; long_count?: number; long_minutes?: number;
};
export const getQueueShortEpisodes = (zoneId: string, from: string, to: string, frontMin: number, kitchenMax = 1) =>
  axiosInstance.get<QueueShortEpisodesResponse>('/core/api/analytics/queue-short-episodes/', {
    params: { zone_id: zoneId, from, to, front_min: frontMin, kitchen_max: kitchenMax },
  });

export const getQueueWorkerExamples = (
  zoneId: string,
  from: string,
  to: string,
  kitchen: number,
  front: number,
  /** Диапазон вместо точной клетки: «за баром ≤1 при очереди ≥4» (Статус → Размер смен, #1863). */
  range?: { kitchenMin?: number; kitchenMax?: number; frontMin?: number; frontMax?: number },
) =>
  axiosInstance.get<{ kitchen: number; front: number; examples: QueueWorkerExample[] }>(
    '/core/api/analytics/queue-worker-examples/',
    { params: { zone_id: zoneId, from, to, kitchen, front, kitchen_min: range?.kitchenMin, kitchen_max: range?.kitchenMax, front_min: range?.frontMin, front_max: range?.frontMax } },
  );

// ── Staff-only "рисовать боксы" debug overlay (#158) ──────────────────────────

export const getFrameBoxes = (frameIds: number[]) =>
  axiosInstance.get<{ boxes_by_frame: Record<number, FrameBox[]> }>(
    '/core/api/analytics/frame-boxes/',
    { params: { frame_ids: frameIds.join(',') } },
  );

// ── Tables: per-table state strips (free / occupied / dirty timeline) ─────────
//
// One colored band per table over the period, BE-aggregated into contiguous
// same-state segments. Gaps between segments = no samples (rendered as the
// strip background). Detail requests (table_id) attach up to 2 crops per
// dirty segment for the fullscreen view.

/** 'unknown' = статус «состояние нельзя определить» (несколько столов / перекрыт). */
export type TableStripState = 'free' | 'occupied' | 'dirty' | 'shifted' | 'unknown' | 'no_data';

export interface TableStripPhoto {
  image_url: string | null;
  /** Полный кадр камеры (если сохранился) — «в оригинальных пропорциях» и удержание в лайтбоксе (Алексей 26.10 #2638). */
  frame_image_url?: string | null;
  /** Кадра рядом нет — ближайший сохранённый кадр камеры (±12 ч): фон рамки «в оригинальных пропорциях» (#2789). */
  bg_frame_image_url?: string | null;
  /** «В оригинальных пропорциях» готовой картинкой (#3082): свой кадр → прямоугольник вокруг стола из
   *  него; нет кадра → кроп развёрнут на полигон, поля чёрные. Окружение с чужого кадра не подставляется. */
  orig_image_url?: string | null;
  at: string;
  /** The crop shows the table dirty (incident frame). */
  dirty?: boolean;
  /** Per-frame verdict the crop was matched to its segment by. */
  verdict?: TableStripState;
  /** Кадр под статусом «нельзя определить»: причина статуса в этот момент. */
  reason?: 'several_tables' | 'obstructed' | 'glare' | null;
  /** Длительность отрезка полоски под фото, с («не убран 5 мин», #3132). */
  segment_s?: number | null;
}

export interface TableStripSegment {
  state: TableStripState;
  start: string; // tz-aware ISO
  end: string;
  /** Only for state 'unknown': why the state could not be determined. */
  reason?: 'several_tables' | 'obstructed' | 'glare' | null;
  /** Пояснение к состоянию — «вещи гостя», «проверено: чисто». */
  sub_reason?: string | null;
  /** «Нельзя определить» рисуется штриховкой. */
  hatched?: boolean;
}

export interface TableStripTable {
  id: number;
  name: string;
  /** Статус «состояние нельзя определить» (сейчас): с какого момента и причина. */
  spoiled_since?: string | null;
  spoiled_reason?: 'several_tables' | 'obstructed' | 'glare' | null;
  /** Полигон стола на кадре, 0..1 (≥3 точек; не обязательно четырёхугольник). */
  polygon?: number[][] | null;
  segments: TableStripSegment[];
  /** Detail mode only: crops spread evenly over the requested window + the
   *  first/last crop of every dirty stretch. */
  photos?: TableStripPhoto[];
}

export interface TablesStripsResponse {
  configured: boolean;
  from: string;
  to: string;
  interval_sec: number;
  working_hours?: WorkingHours | null;
  /** Per-day working windows across the whole range — tells nights/off-hours
   *  apart from genuine no-data gaps and maps a clicked interval to its day. */
  working_hours_by_day?: WorkingHours[];
  tables: TableStripTable[];
}

export const getTablesStrips = (
  zoneId: string,
  from: string,
  to: string,
  tableId?: number | string,
  opts?: { fromTs?: string; toTs?: string; photoCount?: number },
) =>
  axiosInstance.get<TablesStripsResponse>('/core/api/analytics/tables-strips/', {
    params: {
      zone_id: zoneId,
      from,
      to,
      table_id: tableId,
      from_ts: opts?.fromTs,
      to_ts: opts?.toTs,
      photo_count: opts?.photoCount,
    },
  });
